import { useState } from "react";
import { Transaction } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTransactionAnomaly } from "@/hooks/use-ai-features";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AnomalyDetectionButtonProps {
  transaction: Transaction;
  size?: "default" | "sm";
}

export function AnomalyDetectionButton({ transaction, size = "default" }: AnomalyDetectionButtonProps) {
  const [open, setOpen] = useState(false);
  const { detectAnomaly, anomalyData, isLoading, error } = useTransactionAnomaly(transaction.id);

  const handleCheckAnomaly = () => {
    detectAnomaly();
    setOpen(true);
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size={size === "sm" ? "sm" : "icon"}
        onClick={handleCheckAnomaly}
        title="Check for anomalies"
        className={size === "sm" ? "h-7 w-7 p-0" : ""}
      >
        <AlertTriangle className={size === "sm" ? "h-3.5 w-3.5 text-amber-500" : "h-4 w-4 text-amber-500"} />
        <span className="sr-only">Check for anomalies</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Anomaly Analysis</DialogTitle>
            <DialogDescription>
              AI-powered anomaly detection for transaction #{transaction.id}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Analyzing transaction...</span>
              </div>
            ) : error ? (
              <div className="p-4 border border-destructive/30 bg-destructive/10 rounded-md text-destructive">
                <p className="font-medium">Error analyzing transaction</p>
                <p className="text-sm mt-1">{error.message}</p>
              </div>
            ) : anomalyData?.success === false ? (
              <div className="p-4 border border-amber-300 bg-amber-50 rounded-md text-amber-800">
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  Analysis unavailable
                </div>
                <p className="text-sm mt-1">{anomalyData.message || "Unable to complete anomaly analysis at this time."}</p>
              </div>
            ) : anomalyData ? (
              <div className="space-y-4">
                {/* Status badge + confidence */}
                <div className="flex items-center space-x-2">
                  <Badge variant={anomalyData.isAnomaly ? "destructive" : "outline"}>
                    {anomalyData.isAnomaly ? "Anomaly Detected" : "No Anomaly Detected"}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Confidence: {(anomalyData.confidence * 100).toFixed(0)}%
                  </span>
                </div>

                {/* Adjusted: explain the override clearly */}
                {anomalyData.adjusted && (
                  <div className="p-3 border border-blue-200 bg-blue-50 rounded-md space-y-2">
                    <p className="text-sm text-blue-900 font-medium">Adjusted by system rules</p>
                    <p className="text-xs text-blue-800">
                      AI initially flagged this transaction as a potential anomaly, but based on accounting rules it is considered normal.
                    </p>
                    {anomalyData.adjustedReason && (
                      <p className="text-xs text-blue-700 italic">
                        Rule: {anomalyData.adjustedReason}
                      </p>
                    )}
                  </div>
                )}

                {/* AI explanation */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">
                    {anomalyData.adjusted ? "AI Analysis (overridden):" : "Analysis:"}
                  </h4>
                  <p className="text-sm text-muted-foreground">{anomalyData.explanation}</p>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}